<template>
  <a-base-component :subscribed_modules="subscribed_modules">
    <template v-slot:default>
      <v-btn icon @click="show_import_columns = true" class="mt-7">
        <v-icon>mdi-cog-outline</v-icon>
      </v-btn>
      <!-- SHOW IMPORT COLUMNS -->
      <v-dialog
        v-model="show_import_columns"
        :fullscreen="$vuetify.breakpoint.xsOnly"
        max-width="800"
        eager
        retain-focus
      >
        <v-card>
          <!-- Header fijo con título y botón de cerrar -->
          <v-card-title class="py-4 px-6 d-flex align-center">
            <h3 class="headline mb-0">
              {{ $t("configure_reservation_import") }}
            </h3>
            <v-spacer></v-spacer>
            <v-btn icon @click="show_import_columns = false" aria-label="Close">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </v-card-title>
          <v-divider></v-divider>

          <!-- Sistema de pestañas -->
          <v-tabs
            v-model="activeTab"
            background-color="transparent"
            slider-size="2"
            light
            grow
          >
            <v-tabs-slider></v-tabs-slider>
            <v-tab key="columns">
              {{ $t("columns").toUpperCase() }}
            </v-tab>
            <v-tab key="rows">
              {{ $t("rows").toUpperCase() }}
            </v-tab>
            <v-tab key="general">
              {{ $t("general").toUpperCase() }}
            </v-tab>
          </v-tabs>

          <!-- Contenido de las pestañas -->
          <v-tabs-items v-model="activeTab">
            <v-tab-item key="columns">
              <v-card-text class="pa-6">
                <v-data-table
                  :headers="headers"
                  :items="items"
                  hide-default-footer
                  disable-pagination
                  dense
                  :item-class="getDatatableRowClass"
                  class="columns-table"
                >
                  <template v-slot:[`item.index`]="{ item }">
                    <b class="mr-1">{{ item.index }}</b>
                  </template>
                  <template v-slot:[`item.value`]="{ item }">
                    <v-edit-dialog
                      :return-value.sync="item.code"
                      @save="save"
                      @open="initEditDialog(item)"
                      @cancel="clearEditDialog"
                    >
                      <template v-slot:input>
                        <small class="mt-5">{{ $t("column_code") }}</small>
                        <v-combobox
                          v-model="editingColumnCode"
                          :items="available_column_codes"
                          :placeholder="$t('select_or_type_column_code')"
                          :menu-props="{
                            attach: false,
                            contentClass: 'combobox-menu-portal',
                            offsetY: true,
                            closeOnContentClick: true,
                            maxHeight: 200,
                            zIndex: 10001
                          }"
                          outlined
                          dense
                          rounded
                          single-line
                          class="mt-5 mb-5"
                          hide-details
                          eager
                          @input="onComboboxInput"
                          @change="onComboboxChange"
                          ref="columnCodeCombobox"
                        >
                          <template v-slot:item="{ item: codeItem }">
                            <div class="d-flex align-center">
                              <v-icon small class="mr-2 secondary--text"
                                >mdi-table-column</v-icon
                              >
                              <span>{{ codeItem }}</span>
                            </div>
                          </template>
                        </v-combobox>
                      </template>
                      <div v-if="item.type">
                        <v-chip color="secondary" outlined small
                          >{{ $t(item.type) }}: <b>{{ item.value }}</b>
                        </v-chip>
                        {{ item.value }}
                      </div>
                      <span v-else-if="item.value !== ''">{{
                        item.value
                      }}</span>
                      <span v-else class="grey--text text-lighten-1 font-italic"
                        >-- {{ $t("not_used") }} --</span
                      >
                    </v-edit-dialog>
                  </template>
                  <template v-slot:[`item.date_format`]="{ item }">
                    <!-- large: el combobox es menja la tecla Enter, així que
                         cal un botó de desar explícit -->
                    <v-edit-dialog
                      v-if="item.is_date"
                      large
                      :save-text="$t('save')"
                      :cancel-text="$t('cancel')"
                      :return-value.sync="item.date_format"
                      @save="saveColumnDateFormat(item)"
                      @open="initDateFormatDialog(item)"
                      @cancel="clearDateFormatDialog"
                    >
                      <template v-slot:input>
                        <small class="mt-5">{{ $t("date_format") }}</small>
                        <v-combobox
                          v-model="editingDateFormat"
                          :items="available_date_formats"
                          :placeholder="$t('date_format_default_placeholder')"
                          :hint="date_format_hint"
                          persistent-hint
                          clearable
                          :menu-props="{
                            attach: false,
                            contentClass: 'combobox-menu-portal',
                            offsetY: true,
                            closeOnContentClick: true,
                            maxHeight: 200,
                            zIndex: 10001
                          }"
                          outlined
                          dense
                          rounded
                          single-line
                          class="mt-5 mb-5"
                        >
                          <template v-slot:item="{ item: formatItem }">
                            <div
                              class="d-flex align-center justify-space-between"
                              style="width:100%"
                            >
                              <span>{{ formatItem }}</span>
                              <small class="grey--text ml-4">{{
                                formatExample(formatItem)
                              }}</small>
                            </div>
                          </template>
                        </v-combobox>
                      </template>
                      <div v-if="item.date_format">
                        <v-chip color="secondary" outlined small>
                          {{ item.date_format }}
                        </v-chip>
                        <small class="grey--text ml-1">{{
                          formatExample(item.date_format)
                        }}</small>
                      </div>
                      <span
                        v-else
                        class="grey--text text--lighten-1 font-italic"
                      >
                        -- {{ $t("by_default") }}: {{ default_date_format }} --
                      </span>
                    </v-edit-dialog>
                  </template>
                  <template v-slot:[`item.actions`]="{ item }">
                    <v-tooltip bottom dark color="#333333">
                      <template v-slot:activator="{ on }">
                        <v-btn
                          @click.stop="deleteColumn(item.index - 1)"
                          icon
                          color="secondary"
                          v-on="on"
                        >
                          <v-icon small>mdi-trash-can-outline</v-icon>
                        </v-btn>
                      </template>
                      <span>
                        {{ $t("delete_column") }}
                      </span>
                    </v-tooltip>
                  </template>
                </v-data-table>
                <div class="d-flex justify-center mt-4">
                  <v-btn color="secondary" rounded @click="addColumn">
                    <v-icon small>mdi-plus</v-icon>
                    {{ $t("add_column") }}
                  </v-btn>
                </div>
              </v-card-text>
            </v-tab-item>
            <v-tab-item key="rows">
              <v-card-text class="pa-6">
                <div class="d-flex justify-space-between align-center mb-4">
                  <p class="mb-0 text-body-1">
                    {{ $t("ignore_rows_criteria") }}
                  </p>
                  <v-btn
                    color="secondary"
                    rounded
                    @click="addIgnoreRow"
                    :disabled="availableColumns.length === 0"
                  >
                    <v-icon small>mdi-plus</v-icon>
                    {{ $t("add_ignore_row") }}
                  </v-btn>
                </div>
                <v-data-table
                  :headers="ignoreRowsHeaders"
                  :items="ignoreRowsItems"
                  hide-default-footer
                  disable-pagination
                  dense
                  :item-class="getDatatableRowClass"
                  class="ignore-rows-table"
                >
                  <template v-slot:[`item.column`]="{ item }">
                    <v-edit-dialog
                      :return-value.sync="item.column"
                      @save="saveIgnoreRows"
                    >
                      <template v-slot:input>
                        <small class="mt-5">{{ $t("column") }}</small>
                        <v-select
                          v-model="item.column"
                          :items="availableColumns"
                          outlined
                          rounded
                          dense
                          class="mt-5"
                        ></v-select>
                      </template>
                      <span v-if="item.column !== ''">{{
                        $t(item.column)
                      }}</span>
                      <span v-else class="grey--text text-lighten-1 font-italic"
                        >-- {{ $t("not_used") }} --</span
                      >
                    </v-edit-dialog>
                  </template>
                  <template v-slot:[`item.value`]="{ item }">
                    <v-edit-dialog
                      :return-value.sync="item.value"
                      @save="saveIgnoreRows"
                    >
                      <template v-slot:input>
                        <small class="mt-5">{{ $t("value") }}</small>
                        <v-text-field
                          v-model="item.value"
                          outlined
                          dense
                          rounded
                          single-line
                          label="Edit"
                          class="mt-5"
                        ></v-text-field>
                      </template>
                      <span v-if="item.value !== ''">{{ item.value }}</span>
                      <span v-else class="grey--text text-lighten-1 font-italic"
                        >-- {{ $t("not_used") }} --</span
                      >
                    </v-edit-dialog>
                  </template>
                  <template v-slot:[`item.actions`]="{ item }">
                    <v-tooltip bottom dark color="#333333">
                      <template v-slot:activator="{ on }">
                        <v-btn
                          @click.stop="deleteIgnoreRow(item.index)"
                          icon
                          color="secondary"
                          v-on="on"
                        >
                          <v-icon small>mdi-trash-can-outline</v-icon>
                        </v-btn>
                      </template>
                      <span>
                        {{ $t("delete_ignore_row") }}
                      </span>
                    </v-tooltip>
                  </template>
                  <template v-slot:no-data>
                    <div class="text-center py-8">
                      <v-icon large class="mb-2 grey--text text--lighten-1"
                        >mdi-table-row</v-icon
                      >
                      <p class="grey--text text--lighten-1">
                        {{ $t("no_ignore_criteria") }}
                      </p>
                    </div>
                  </template>
                </v-data-table>
              </v-card-text>
            </v-tab-item>
            <v-tab-item key="general">
              <v-card-text class="pa-6">
                <p class="text-body-1 mb-1">
                  {{ $t("default_date_format_description") }}
                </p>
                <p class="text-body-2 grey--text text--darken-1 mb-4">
                  {{ $t("column_date_format_description") }}
                </p>
                <v-combobox
                  v-model="editingDefaultDateFormat"
                  :items="available_date_formats"
                  :label="$t('default_date_format')"
                  :hint="date_format_hint"
                  persistent-hint
                  outlined
                  dense
                  rounded
                  @change="saveDefaultDateFormat"
                >
                  <template v-slot:item="{ item: formatItem }">
                    <div
                      class="d-flex align-center justify-space-between"
                      style="width:100%"
                    >
                      <span>{{ formatItem }}</span>
                      <small class="grey--text ml-4">{{
                        formatExample(formatItem)
                      }}</small>
                    </div>
                  </template>
                </v-combobox>
              </v-card-text>
            </v-tab-item>
          </v-tabs-items>
        </v-card>
      </v-dialog>
    </template>
    <template v-slot:loading>
      <v-skeleton-loader type="avatar" class="mt-5"></v-skeleton-loader>
    </template>
  </a-base-component>
</template>

<script>
import { mapGetters, mapState } from "vuex";
import * as Utils from "@/utils";
import Vue from "vue";
import ABaseComponent from "@/components/aBaseComponent.vue";

export default {
  name: "import-reservation-columns-config",
  components: { ABaseComponent },
  data: () => ({
    subscribed_modules: [
      "dataimport_revenue_reservation_columns",
      "dataimport_revenue_reservation_ignore_rows",
      "dataimport_revenue_reservation_available_column_codes",
      "dataimport_revenue_reservation_date_formats"
    ],
    show_import_columns: false,
    activeTab: 0,
    editingColumnCode: "",
    editingItem: null,
    editingDateFormat: "",
    editingDateFormatItem: null,
    editingDefaultDateFormat: "",
    // Dia > 12 perquè es distingeixi d/m/Y de m/d/Y a cop d'ull
    date_format_sample: "2025-12-31 14:05:09"
  }),
  computed: {
    headers: function() {
      return [
        {
          text: this.$t("column"),
          value: "index",
          align: "center",
          sortable: false
        },
        {
          text: this.$t("value"),
          value: "value",
          align: "left",
          sortable: false
        },
        {
          text: this.$t("date_format"),
          value: "date_format",
          align: "left",
          sortable: false
        },
        {
          text: "",
          value: "actions",
          align: "left",
          sortable: false,
          width: "120px"
        }
      ];
    },
    items: function() {
      return this.reservation_columns.map((column, index) => {
        return {
          index: index + 1,
          value: this.$t(column),
          code: column,
          original_code: column,
          is_date: this.column_data_formats[column] === "date",
          date_format: this.column_date_formats[column] || ""
        };
      });
    },
    // Format que s'aplica a les columnes de data que no en tenen un d'específic
    default_date_format: function() {
      return this.date_formats_config.date_format || "";
    },
    column_date_formats: function() {
      return this.date_formats_config.date_formats || {};
    },
    column_data_formats: function() {
      return this.date_formats_config.column_data_formats || {};
    },
    available_date_formats: function() {
      return this.date_formats_config.available_date_formats || [];
    },
    date_format_hint: function() {
      return this.$t("date_format_hint");
    },
    ignoreRowsHeaders: function() {
      return [
        {
          text: this.$t("column"),
          value: "column",
          align: "left",
          sortable: false
        },
        {
          text: this.$t("value"),
          value: "value",
          align: "left",
          sortable: false
        },
        {
          text: "",
          value: "actions",
          align: "left",
          sortable: false,
          width: "120px"
        }
      ];
    },
    ignoreRowsItems: function() {
      return this.reservation_ignore_rows.map((row, index) => {
        return {
          index: index,
          column: row.column || "",
          value: row.value || "",
          original_column: row.column || "",
          original_value: row.value || ""
        };
      });
    },
    availableColumns: function() {
      return this.reservation_columns.filter(column => column && column !== "");
    },
    // Computed con datos de prueba para verificar que funciona
    testColumnCodes: function() {
      const serverCodes = this.available_column_codes || [];
      const testCodes = [
        "room",
        "guest_name",
        "email",
        "phone",
        "check_in",
        "check_out",
        "nights",
        "rate"
      ];
      return [...serverCodes, ...testCodes].filter(
        (value, index, self) => self.indexOf(value) === index
      );
    },
    ...mapState({
      reservation_columns: state =>
        state.dataimport.revenue_reservation_columns,
      reservation_ignore_rows: state =>
        state.dataimport.revenue_reservation_ignore_rows,
      available_column_codes: state =>
        state.dataimport.revenue_reservation_available_column_codes,
      date_formats_config: state =>
        state.dataimport.revenue_reservation_date_formats
    }),
    ...mapGetters({
      current_hotel: "current_hotel",
      get_kpi: "get_kpi"
    })
  },
  methods: {
    getDatatableRowClass() {
      return "a-datatable-row";
    },
    save() {
      // Si estamos editando un item específico, usar el valor del combobox
      if (this.editingItem && this.editingColumnCode !== undefined) {
        const index = this.editingItem.index - 1;
        this.reservation_columns.splice(index, 1, this.editingColumnCode);
        this.$store.dispatch("updateRevenueReservationColumn", {
          hotel_id: this.current_hotel.id,
          index: index,
          code: this.editingColumnCode
        });

        // Limpiar variables de edición
        this.editingItem = null;
        this.editingColumnCode = "";

        Utils.showSuccess(this, "columns_updated_ok", "columns_updated");
        return;
      }

      // Fallback para el comportamiento original
      for (let item of this.items) {
        if (item.code !== item.original_code) {
          this.reservation_columns.splice(item.index - 1, 1, item.code);
          this.$store.dispatch("updateRevenueReservationColumn", {
            hotel_id: this.current_hotel.id,
            index: item.index - 1,
            code: item.code
          });
        }
      }
      Utils.showSuccess(this, "columns_updated_ok", "columns_updated");
    },
    // Converteix un format de data de PHP (el que desa el backend) a moment.js
    // per poder-ne mostrar un exemple al client.
    phpDateFormatToMoment(format) {
      const tokens = {
        d: "DD",
        j: "D",
        m: "MM",
        n: "M",
        M: "MMM",
        F: "MMMM",
        Y: "YYYY",
        y: "YY",
        H: "HH",
        G: "H",
        i: "mm",
        s: "ss"
      };
      let result = "";
      for (let i = 0; i < format.length; i++) {
        const char = format[i];
        if (char === "\\") {
          // Caràcter escapat: es mostra tal qual
          result += "[" + (format[++i] || "") + "]";
        } else if (tokens[char] !== undefined) {
          result += tokens[char];
        } else if (/[A-Za-z]/.test(char)) {
          result += "[" + char + "]";
        } else {
          result += char;
        }
      }
      return result;
    },
    formatExample(format) {
      if (!format) {
        return "";
      }
      // En anglès perquè els noms de mes de PHP sempre ho són, sigui quin
      // sigui l'idioma de l'usuari
      return Vue.moment(this.date_format_sample)
        .locale("en")
        .format(this.phpDateFormatToMoment(format));
    },
    initDateFormatDialog(item) {
      this.editingDateFormat = item.date_format;
      this.editingDateFormatItem = item;
    },
    clearDateFormatDialog() {
      this.editingDateFormat = "";
      this.editingDateFormatItem = null;
    },
    saveColumnDateFormat(item) {
      const code = this.editingDateFormatItem
        ? this.editingDateFormatItem.code
        : item.code;
      this.updateDateFormat(code, this.editingDateFormat);
      this.clearDateFormatDialog();
    },
    saveDefaultDateFormat(value) {
      this.updateDateFormat("", value);
    },
    updateDateFormat(code, format) {
      this.$store
        .dispatch("updateRevenueReservationDateFormat", {
          hotel_id: this.current_hotel.id,
          code: code,
          format: format || ""
        })
        .then(() => {
          Utils.showSuccess(this, "columns_updated_ok", "columns_updated");
        })
        .catch(() => {
          // El backend rebutja els formats que no serveixen per llegir dates
          Utils.showError(this, "invalid_date_format");
          this.editingDefaultDateFormat = this.default_date_format;
        });
    },
    initEditDialog(item) {
      // Inicializar la edición con el valor actual
      this.editingColumnCode = item.code;
      this.editingItem = item;
    },
    onComboboxInput(value) {
      // Actualizar el valor mientras se escribe
      this.editingColumnCode = value;
      if (this.editingItem) {
        this.editingItem.code = value;
      }
    },
    onComboboxChange(value) {
      // Actualizar cuando se selecciona un valor
      this.editingColumnCode = value;
      if (this.editingItem) {
        this.editingItem.code = value;
      }
    },
    clearEditDialog() {
      // Limpiar variables cuando se cancela
      this.editingItem = null;
      this.editingColumnCode = "";
    },
    addColumn() {
      // Añadir una nueva columna vacía
      this.reservation_columns.push("");

      // Actualizar en la API
      this.$store.dispatch("updateRevenueReservationColumn", {
        hotel_id: this.current_hotel.id,
        index: this.reservation_columns.length - 1,
        code: ""
      });
    },
    deleteColumn(index) {
      // Eliminar columna
      this.reservation_columns.splice(index, 1);

      // Actualizar toda la lista en la API (ya que los índices han cambiado)
      this.saveAllColumns();
    },
    saveAllColumns() {
      // Guardar todas las columnas de una vez
      this.$store.dispatch("updateAllRevenueReservationColumns", {
        hotel_id: this.current_hotel.id,
        columns: this.reservation_columns
      });
    },
    saveIgnoreRows() {
      for (let item of this.ignoreRowsItems) {
        if (
          item.column !== item.original_column ||
          item.value !== item.original_value
        ) {
          // Update the store data directly
          this.reservation_ignore_rows.splice(item.index, 1, {
            column: item.column,
            value: item.value
          });

          this.$store.dispatch("updateRevenueReservationIgnoreRow", {
            hotel_id: this.current_hotel.id,
            index: item.index,
            column: item.column,
            value: item.value
          });
        }
      }
      Utils.showSuccess(this, "columns_updated_ok", "columns_updated");
    },
    addIgnoreRow() {
      // Añadir un nuevo criterio de ignorar filas
      this.reservation_ignore_rows.push({
        column: "",
        value: ""
      });

      // Actualizar en la API
      this.$store.dispatch("updateRevenueReservationIgnoreRow", {
        hotel_id: this.current_hotel.id,
        index: this.reservation_ignore_rows.length - 1,
        column: "",
        value: ""
      });
    },
    deleteIgnoreRow(index) {
      // Eliminar criterio de ignorar filas
      this.reservation_ignore_rows.splice(index, 1);

      // Actualizar toda la lista en la API (ya que los índices han cambiado)
      this.saveAllIgnoreRows();
    },
    saveAllIgnoreRows() {
      // Guardar todos los criterios de ignorar filas de una vez
      this.$store.dispatch("updateAllRevenueReservationIgnoreRows", {
        hotel_id: this.current_hotel.id,
        ignore_rows: this.reservation_ignore_rows
      });
    }
  },
  watch: {
    default_date_format: {
      immediate: true,
      handler: function(format) {
        this.editingDefaultDateFormat = format;
      }
    }
  },
  mounted() {}
};
</script>

<style lang="scss" scoped>
.columns-table .a-datatable-row > td:last-child {
  display: flex;
  align-items: center;
  padding-right: 55px;
  justify-content: flex-end;
}

.ignore-rows-table .a-datatable-row > td:last-child {
  display: flex;
  align-items: center;
  padding-right: 55px;
  justify-content: flex-end;
}

.v-tabs {
  margin-bottom: 20px;
}

.v-tabs .v-tab {
  font-weight: 600;
  letter-spacing: 0.5px;
}

.v-data-table {
  border-radius: 8px;
}

/* Asegurar que el dropdown del combobox aparezca correctamente */
.v-menu__content {
  z-index: 9999 !important;
}

.v-dialog .v-select__menu .v-menu__content {
  z-index: 10000 !important;
}

.combobox-menu-portal {
  z-index: 10002 !important;
}

/* Específico para el combobox dentro del edit dialog */
.v-edit-dialog .v-menu__content {
  z-index: 10001 !important;
  position: fixed !important;
}
</style>
